import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import { SerpStoreReturnTablesView } from '@/components/Approval/@components/SerpStoreReturn';
import SerpStoreReturnDetailDialog from '@/components/Approval/@components/SerpStoreReturn/DetailDialog';
import { getSerpStoreReturnDraftSourceUrl } from '@/constant/approval';
import { getNestedDataFetcher } from '@/utils/apis';
import type { SerpDocumentRef } from '@/utils/approval/serpDocument';
import type { SerpStoreReturnDraftSource } from '@/utils/approval/serpStoreReturn';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type SerpStoreReturnFieldProps = {
  /** 신규 작성은 진입 쿼리, 편집은 저장 attributes 의 SERP 문서 */
  serpDocument: SerpDocumentRef | null;
};

/**
 * SERP 매장반품 요청(171) — 레거시 appr_edit_forms_serp_shop_return.
 * 편집도 SERP 를 다시 조회해 표를 새로 그림. 제목은 자동 입력 없음(레거시 동일)
 */
const SerpStoreReturnField = ({ serpDocument }: SerpStoreReturnFieldProps) => {
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'serpStoreReturn' });
  const [detailOpen, setDetailOpen] = useState(false);

  const typeCode = serpDocument?.typeCode;
  const sequence = serpDocument?.sequence;
  const { data, error, isLoading } = useSWR<SerpStoreReturnDraftSource>(
    typeCode && sequence
      ? getSerpStoreReturnDraftSourceUrl(typeCode, sequence)
      : null,
    getNestedDataFetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    const current = getValues('serpStoreReturn');
    if (
      current &&
      (current.typeCode !== typeCode || current.sequence !== sequence)
    ) {
      setValue('serpStoreReturn', null, { shouldDirty: false });
    }
  }, [typeCode, sequence, getValues, setValue]);

  useEffect(() => {
    if (!data || !typeCode || !sequence) return;
    if (
      data.attributes.typeCode !== typeCode ||
      data.attributes.sequence !== sequence
    )
      return;
    setValue(
      'serpStoreReturn',
      { typeCode, sequence, source: data.display.source ?? {} },
      { shouldDirty: false },
    );
  }, [data, typeCode, sequence, setValue]);

  const renderBody = () => {
    if (!serpDocument) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('SERP 문서 정보가 없습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (state?.source) {
      return (
        <>
          <SerpStoreReturnTablesView
            source={state.source}
            t={t}
            onOpenDetail={() => setDetailOpen(true)}
          />
          <SerpStoreReturnDetailDialog
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            source={state.source}
          />
        </>
      );
    }
    if (error) {
      return (
        <ApprovalNoticeBox role="alert">
          {t('데이터를 불러오지 못했습니다.')}
        </ApprovalNoticeBox>
      );
    }
    if (isLoading) {
      return (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      );
    }
    return null;
  };

  return <FieldBlock className="serp-store-return">{renderBody()}</FieldBlock>;
};

export default SerpStoreReturnField;
